import React from "react";
import { SkeletonBox, SkeletonText, SkeletonCard, SkeletonButton, SkeletonChip } from "./SkeletonBox";

/**
 * DetailPageSkeleton - Non-animated skeleton for Movie/Series detail pages
 * 
 * Matches layout of:
 * - filmPageContent.js (Background: 600px, metadata layout)
 * - Flutter TvDetailsSkeleton structure
 */

function skeletonElement(className) {
  return function SkeletonElement(props) {
    var mergedClassName = className + (props.className ? " " + props.className : "");
    return <div {...props} className={mergedClassName} />;
  };
}

var Container = skeletonElement("legacy-skeleton-detail");
var BackgroundPlaceholder = skeletonElement("legacy-skeleton-detail-bg");
var ShadeOverlay = skeletonElement("legacy-skeleton-detail-shade");
var ContentArea = skeletonElement("legacy-skeleton-detail-content");
var TitleSection = skeletonElement("legacy-skeleton-detail-title-section");
var MetadataRow = skeletonElement("legacy-skeleton-inline-row legacy-skeleton-detail-metadata-row");
var MetadataItem = skeletonElement("legacy-skeleton-inline-item legacy-skeleton-detail-metadata-item");
var GenreRow = skeletonElement("legacy-skeleton-wrap-row legacy-skeleton-detail-genre-row");
var GenreItem = skeletonElement("legacy-skeleton-chip-wrap");
var DescriptionSection = skeletonElement("legacy-skeleton-detail-description");
var ButtonsRow = skeletonElement("legacy-skeleton-inline-row legacy-skeleton-detail-buttons");
var ButtonWrapper = skeletonElement("legacy-skeleton-inline-item legacy-skeleton-detail-button-wrap");
var RelatedSection = skeletonElement("legacy-skeleton-detail-related");
var RelatedTitle = skeletonElement("legacy-skeleton-detail-related-title");
var CardsContainer = skeletonElement("legacy-skeleton-cards legacy-skeleton-cards-no-pad");
var CardWrapper = skeletonElement("legacy-skeleton-card-wrap");

/**
 * Hero Content Skeleton
 */
function HeroContentSkeleton() {
  return (
    <ContentArea>
      <TitleSection>
        {/* Main Title */}
        <SkeletonText width={400} size="xl" style={{ marginBottom: 12 }} />
        
        {/* English Title */}
        <SkeletonText width={280} size="lg" />
      </TitleSection>
      
      {/* Metadata Row: Year | Country | Duration */}
      <MetadataRow>
        <MetadataItem>
          <SkeletonText width={60} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={2} height={20} borderRadius={1} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={80} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={2} height={20} borderRadius={1} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={100} size="md" />
        </MetadataItem>
      </MetadataRow>
      
      {/* Rating Row: IMDB | Likes */}
      <MetadataRow>
        <MetadataItem>
          <SkeletonBox width={50} height={24} borderRadius={4} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={40} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={24} height={24} circle />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={50} size="md" />
        </MetadataItem>
      </MetadataRow>
      
      {/* Genre Chips */}
      <GenreRow>
        <GenreItem>
          <SkeletonChip width={80} />
        </GenreItem>
        <GenreItem>
          <SkeletonChip width={100} />
        </GenreItem>
        <GenreItem>
          <SkeletonChip width={90} />
        </GenreItem>
      </GenreRow>
      
      {/* Description */}
      <DescriptionSection>
        <SkeletonText width="100%" size="md" style={{ marginBottom: 8 }} />
        <SkeletonText width="100%" size="md" style={{ marginBottom: 8 }} />
        <SkeletonText width="70%" size="md" />
      </DescriptionSection>
      
      {/* Action Buttons */}
      <ButtonsRow>
        <ButtonWrapper>
          <SkeletonButton variant="primary" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="primary" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
      </ButtonsRow>
    </ContentArea>
  );
}

/**
 * Related Content Skeleton
 */
function RelatedContentSkeleton({ cardCount = 6 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <CardWrapper key={"related_" + i}>
        <SkeletonCard type="portrait" />
      </CardWrapper>
    );
  }
  
  return (
    <RelatedSection>
      <RelatedTitle />
      <CardsContainer>
        {cards}
      </CardsContainer>
    </RelatedSection>
  );
}

/**
 * Artist Slider Skeleton
 */
function ArtistSliderSkeleton({ count = 6 }) {
  var artists = [];
  for (var i = 0; i < count; i++) {
    artists.push(
      <CardWrapper key={"artist_" + i}>
        <SkeletonCard type="artist" />
      </CardWrapper>
    );
  }
  
  return (
    <RelatedSection>
      <RelatedTitle />
      <CardsContainer>
        {artists}
      </CardsContainer>
    </RelatedSection>
  );
}

/**
 * DetailPageSkeleton - Main export
 */
function DetailPageSkeleton({ showRelated = true, showArtists = true }) {
  return (
    <Container>
      <BackgroundPlaceholder />
      <ShadeOverlay />
      
      <HeroContentSkeleton />
      
      {showArtists && <ArtistSliderSkeleton count={6} />}
      {showRelated && <RelatedContentSkeleton cardCount={6} />}
    </Container>
  );
}

export { HeroContentSkeleton, RelatedContentSkeleton, ArtistSliderSkeleton };
export default DetailPageSkeleton;
